import { useState, useRef } from 'react';
import { readSheet, buildRows, importRows, downloadTemplate, downloadCsv } from '../lib/bulkImport';
import { sendOrgMail } from '../lib/org';

// Importación masiva de personas desde Excel/CSV, con validación previa.
export default function BulkImport({ locations, areas, existingEmails, onClose, onDone }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [onlyErr, setOnlyErr] = useState(false);
  const [welcome, setWelcome] = useState(true);
  const [busy, setBusy] = useState(false);
  const [prog, setProg] = useState([0, 0]);
  const [res, setRes] = useState(null);
  const inp = useRef();

  const pick = async (f) => {
    if (!f) return;
    setErr(''); setRows(null);
    try { setRows(buildRows(await readSheet(f), { locations, areas, existingEmails })); }
    catch (e) { setErr(e.message || 'No se pudo leer el archivo'); }
  };
  const bad = rows ? rows.filter((r) => r.errors.length) : [];
  const good = rows ? rows.filter((r) => !r.errors.length) : [];

  const run = async () => {
    setBusy(true); setErr('');
    try {
      const out = await importRows(good, (a, b) => setProg([a, b]));
      let mailed = 0, mailFail = 0;
      if (welcome) {
        const todo = out.filter((x) => x.ok && x.id && !good.find((g) => g.row === x.row)?.password);
        for (let i = 0; i < todo.length; i += 5) {
          await Promise.all(todo.slice(i, i + 5).map((x) => sendOrgMail('invite', x.id).then(() => { mailed++; }).catch(() => { mailFail++; })));
        }
      }
      setRes({ out, mailed, mailFail });
      onDone?.();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const shown = rows ? (onlyErr ? bad : rows) : [];
  const okN = res ? res.out.filter((x) => x.ok).length : 0;
  const results = () => downloadCsv('resultado-importacion.csv', [['Fila', 'Nombre', 'Email', 'Resultado', 'Contraseña temporal / Error'],
    ...res.out.map((x) => [x.row, good.find((g) => g.row === x.row)?.name || '', x.email || good.find((g) => g.row === x.row)?.email || '', x.ok ? 'Creado' : 'Error', x.ok ? (x.password || '(la definiste vos)') : x.error])]);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center sm:p-4" onClick={() => !busy && onClose()}>
      <div className="bg-white w-full sm:max-w-4xl rounded-t-3xl sm:rounded-3xl max-h-[92vh] overflow-y-auto p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div><h3 className="text-lg font-bold text-gray-900">Importar personas desde Excel</h3><p className="text-xs text-gray-400">Hasta 300 por vez. Revisás todo antes de crear nada.</p></div>
          <button onClick={() => !busy && onClose()} className="text-gray-400 text-xl leading-none">×</button>
        </div>

        {res ? (
          <div className="space-y-3">
            <div className="rounded-2xl bg-teal-50 text-teal-800 p-4 text-sm font-bold">Se crearon {okN} de {res.out.length} personas.{res.mailed > 0 && ` Se enviaron ${res.mailed} mails de bienvenida.`}{res.mailFail > 0 && ` ${res.mailFail} mails no se pudieron enviar (podés reenviarlos desde cada ficha).`}</div>
            {res.out.some((x) => !x.ok) && <div className="rounded-2xl bg-red-50 p-3 text-xs text-red-700 space-y-1 max-h-40 overflow-y-auto">{res.out.filter((x) => !x.ok).map((x) => <p key={x.row}>Fila {x.row}: {x.error}</p>)}</div>}
            <p className="text-xs text-amber-700 bg-amber-50 rounded-xl px-3 py-2">El archivo de resultados incluye contraseñas temporales. Guardalo en un lugar seguro y no lo reenvíes.</p>
            <div className="flex gap-2"><button onClick={results} className="flex-1 py-2.5 rounded-xl text-sm font-bold border border-gray-200 text-gray-700">Descargar resultado</button><button onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-sky-700">Listo</button></div>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 items-center">
              <button onClick={downloadTemplate} className="px-3 py-2 rounded-xl text-sm font-bold border border-gray-200 text-gray-700">1. Bajar plantilla</button>
              <button onClick={() => inp.current?.click()} disabled={busy} className="px-3 py-2 rounded-xl text-sm font-bold text-white bg-sky-700">2. Subir Excel o CSV</button>
              <input ref={inp} type="file" accept=".xlsx,.csv,.txt" className="hidden" onChange={(e) => { pick(e.target.files[0]); e.target.value = ''; }} />
              <span className="text-xs text-gray-400">Nombre y email son obligatorios. El resto se puede completar después.</span>
            </div>
            {err && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{err}</p>}
            {rows && (
              <>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-bold text-teal-700 bg-teal-50 px-3 py-1 rounded-lg">{good.length} listas</span>
                  <span className={`font-bold px-3 py-1 rounded-lg ${bad.length ? 'text-red-700 bg-red-50' : 'text-gray-400 bg-gray-50'}`}>{bad.length} con errores (se omiten)</span>
                  {bad.length > 0 && <label className="text-xs text-gray-500 flex items-center gap-1 ml-auto"><input type="checkbox" checked={onlyErr} onChange={(e) => setOnlyErr(e.target.checked)} />Ver solo errores</label>}
                </div>
                <div className="overflow-x-auto border border-gray-100 rounded-2xl max-h-[40vh] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-gray-50"><tr className="text-left text-gray-400"><th className="p-2">Fila</th><th>Nombre</th><th>Email</th><th>Sede</th><th>Pago</th><th>Observaciones</th></tr></thead>
                    <tbody>{shown.map((r) => (
                      <tr key={r.row} className={`border-t border-gray-50 ${r.errors.length ? 'bg-red-50/50' : ''}`}>
                        <td className="p-2 text-gray-400">{r.row}</td><td className="font-bold text-gray-800">{r.name}</td><td>{r.email}</td><td>{r.locations?.length ? r.locations.join(" · ") : "—"}</td>
                        <td>{r.pay_type === 'hourly' ? 'Por hora' : 'Mensual'}</td>
                        <td>{r.errors.map((e, i) => <p key={i} className="text-red-600 font-bold">✕ {e}</p>)}{r.warnings.map((e, i) => <p key={i} className="text-amber-600">! {e}</p>)}{!r.errors.length && !r.warnings.length && <span className="text-teal-600">✓</span>}</td>
                      </tr>))}</tbody>
                  </table>
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-600"><input type="checkbox" checked={welcome} onChange={(e) => setWelcome(e.target.checked)} />Enviar mail de bienvenida con acceso a quienes no tienen contraseña definida</label>
                {busy && <div className="h-2 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-sky-600 transition-all" style={{ width: `${prog[1] ? (prog[0] / prog[1]) * 100 : 5}%` }} /></div>}
                <button onClick={run} disabled={busy || !good.length} className="w-full py-3 rounded-xl text-sm font-bold text-white bg-sky-700 disabled:opacity-40">{busy ? `Creando… ${prog[0]}/${prog[1]}` : `Crear ${good.length} persona${good.length === 1 ? '' : 's'}`}</button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
